import React, {
  createContext,
  useState,
  useMemo,
  type Dispatch,
  type PropsWithChildren,
  type SetStateAction,
  useEffect,
} from 'react';
import qs from 'qs';

import { getApiAutoCompleteSuggestions } from '@uncinc/uncinc-react-kitchen-sink';

import { AutocompleteHit } from '../../types';
import { API_URL } from '../../../../config/constants';

const getSuggestionsSetResults = async (value: string, type: string) => {
  const endpoint = `${API_URL}/api/autocomplete`;
  const queryParameter = 'q';
  const requestOptions = {
    headers: {
      'Content-Type': 'application/json',
    },
  };

  return getApiAutoCompleteSuggestions(endpoint, queryParameter, value, requestOptions)
    .then((data: AutocompleteHit[]) => data.find((hit) => hit.type === type))
    // eslint-disable-next-line no-console
    .catch(() => console.error('Error fetching autocomplete suggestions'));
};

type MapFiltersContext = {
  hit: [AutocompleteHit | undefined, Dispatch<SetStateAction<AutocompleteHit | undefined>>];
  radius: [number | undefined, Dispatch<SetStateAction<number | undefined>>];
  types: [string[], Dispatch<SetStateAction<string[]>>];
};

const FiltersContext = createContext<MapFiltersContext>({
  hit: [undefined, () => {}],
  radius: [undefined, () => {}],
  types: [[], () => {}],
});

export const FiltersContextProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [hit, setHit] = useState<AutocompleteHit | undefined>();
  const [radius, setRadius] = useState<number | undefined>();
  const [types, setTypes] = useState<string[]>([]);

  const providerValue = useMemo<MapFiltersContext>(() => ({
    hit: [hit, setHit],
    radius: [radius, setRadius],
    types: [types, setTypes],
  }), [hit, radius, types]);

  // Fetch initial values from URL
  useEffect(() => {
    let hitName: string | undefined;
    let hitType: string | undefined;
    const parsed = qs.parse(window.location.search.slice(1), { arrayLimit: 10 });

    // Parse filters from URL
    if (Array.isArray(parsed.filters)) {
      parsed.filters.forEach((filter) => {
        if (!filter) {
          return;
        }

        Object.entries(filter).forEach(([key, value]) => {
          switch (key) {
            case 'hitName':
              hitName = String(value);
              return;
            case 'hitType':
              hitType = String(value);
              return;
            case 'radius':
              setRadius(Number(value));
              return;
            case 'types':
              setTypes((Array.isArray(value) ? value : [value]) as string[]);
            // no default
          }
        });
      });
    }

    if (hitName && hitType) {
      getSuggestionsSetResults(hitName, hitType)
        .then((result) => {
          if (result) setHit(result);
        })
        .catch(() => {
          // eslint-disable-next-line no-console
          console.error('Error fetching autocomplete suggestions');
        });
    }
  }, []);

  // Update URL with filters
  useEffect(() => {
    const values = {
      hitName: hit ? hit.name : undefined,
      hitType: hit ? hit.type : undefined,
      radius,
      types,
    };
    const search = qs.stringify({ filters: [values] }, { arrayFormat: 'brackets' });
    window.history.replaceState({}, '', `${window.location.pathname}?${search}`);
  }, [hit, radius, types]);

  return (
    <FiltersContext.Provider value={providerValue}>
      {children}
    </FiltersContext.Provider>
  );
};

export const LeafletMapContextConsumer = FiltersContext.Consumer;

export default FiltersContext;
